import React from "react";
import { useTranslations } from "i18n";
import {
  BannerCoinContainer,
  BannerContainer,
  BannerContent,
  BannerMeta,
  BannerMetaContentWrapper,
  CoinImageContainer,
  FooterSectionContainer,
  FooterSectionImageContainer,
  TokenBuyLinkBtn
} from "./style";
import Image from "next/image";
import BannerBgImg from "public/static/images/howToBuy/banner_bg_img.svg";
import FooterSectionImg from "public/static/images/howToBuy/banner_footer_sec_image.png";
import { Col, Grid, Row } from "components/layout/Grid";
import NextLink from "components/parts/NextLink";
import PriceIndicator from "./PriceIndicator";
import type { PriceOverview } from "types/prices";
import { getIcon } from "utils/currencies/icon";
import { FILES } from "i18n/constants";
import PromoRibbon from "components/parts/PromoRibbon";
import { Colors } from "themes/color-palette";
import {
  FontWeight,
  HubbleBody,
  HubbleDisplay,
  HubbleHeading
} from "components/parts/Typography";
import { Text } from "@hubble/web";
import { useCountryStateCookies } from "hooks/cookies/useCountryStateCookies";

interface BuyingGuideBannerComponentProps {
  coinName: string;
  coinSymbol: string;
  lastUpdated: string;
  slug: string;
  priceOverview: PriceOverview;
  isMobile?: boolean;
}

const BuyingGuideBanner = (props: BuyingGuideBannerComponentProps) => {
  const { coinName, coinSymbol, lastUpdated, priceOverview, isMobile } = props;
  const t = useTranslations(FILES.HOW_TO_BUY);
  const { isUK } = useCountryStateCookies();
  const promoSection = {
    text: t("promoText"),
    linkLabel: t("promoLinkLabel"),
    linkHref: "/derivatives"
  };
  return (
    <BannerContainer>
      <BannerMeta>
        <BannerMetaContentWrapper
          justify="space-between"
          align="center"
          wrap="wrap"
        >
          <HubbleBody
            style={{ fontSize: "12px" }}
            size="sm"
            color={Colors.gray[600]}
          >
            {t("howToBuyGuide")}
          </HubbleBody>
          <HubbleBody
            style={{ fontSize: "12px" }}
            size="sm"
            color={Colors.gray[600]}
          >
            {t("lastUpdated")}: {lastUpdated}
          </HubbleBody>
        </BannerMetaContentWrapper>
      </BannerMeta>
      <Grid>
        <Row
          noMargin
          style={{ marginLeft: "0", marginRight: "0", overflow: "hidden" }}
        >
          <Col sm={12} md={12} justifyContent="center" flexDirection="column">
            <BannerContent>
              <HubbleDisplay as={"h1"} size="lg" bold color={Colors.gray[600]}>
                {t("howToBuy")} <br /> {coinName} ({coinSymbol})
              </HubbleDisplay>
              <PriceIndicator {...priceOverview} />
              <TokenBuyLinkBtn isBlue>
                <NextLink
                  id={`${coinName}-how-to-buy-register`}
                  href="https://exchange.gemini.com/register"
                  prefetch={false}
                >
                  {t("buy")} {coinName}
                </NextLink>
              </TokenBuyLinkBtn>
            </BannerContent>{" "}
            <BannerCoinContainer>
              <Image
                priority
                alt={t("buyingGuideBannerBg")}
                className="buying guide banner-bg"
                src={BannerBgImg}
                fill
              />
              <CoinImageContainer>
                <Image
                  priority
                  alt={t("coinImage", { coinName })}
                  className="buying-guide__coin-img"
                  src={getIcon(coinSymbol?.toLowerCase())}
                  fill
                />
              </CoinImageContainer>
            </BannerCoinContainer>
            {/* 🏁 Start: Footer section  */}
            <FooterSectionContainer>
              <div>
                <HubbleDisplay
                  as={"h2"}
                  mb={2}
                  align="left"
                  size="md"
                  bold
                  color={Colors.black}
                >
                  {t("footerSectionTitle")}
                </HubbleDisplay>
                <Text.Body size="xs" italic align="left" mb={2}>
                  {t("footerSectionTitleSubText")}
                </Text.Body>
                <HubbleHeading
                  size="sm"
                  as={"p"}
                  align="left"
                  fontWeight={parseInt(FontWeight.regular)}
                  color={Colors.gray[600]}
                >
                  {t("footerSectionLinkText")}
                </HubbleHeading>
              </div>
              <FooterSectionImageContainer>
                <Image
                  priority
                  alt={t("buyingGuideFooterSectionImg")}
                  className="buying-guide__footer-sec-img"
                  src={FooterSectionImg}
                  fill
                />
              </FooterSectionImageContainer>
            </FooterSectionContainer>
          </Col>
        </Row>
      </Grid>
      {/* 🏁 Start: Promo ribbon section  */}
      {!isUK && <PromoRibbon {...promoSection} isMobile={isMobile} />}
    </BannerContainer>
  );
};
export default BuyingGuideBanner;
